import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import SettingsService from "services/SettingsService";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";
import { notifySuccess } from "jsx/components/CitoPlus/components/CitoPlusToastr";

/**
 * Modal para la política de tratamiento de datos.
 * - Si el texto es una URL, se muestra en un iframe y se ofrece link externo.
 * - Si es HTML, se renderiza con dangerouslySetInnerHTML.
 * - Si es texto plano, se muestra en un párrafo.
 */
const DataTreatmentPolicyModal = ({ show, onAccepted }) => {
  const [policyText, setPolicyText] = useState("");
  const [loading, setLoading] = useState(false);

  const isPolicyUrl = useMemo(() => {
    if (!policyText) return false;
    try {
      const url = new URL(policyText.trim());
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (e) {
      return false;
    }
  }, [policyText]);

  const isPolicyHtml = useMemo(() => {
    if (!policyText) return false;
    return /<\/*[a-z][\s\S]*>/i.test(policyText);
  }, [policyText]);

  useEffect(() => {
    if (!show) return;
    const loadPolicy = async () => {
      try {
        setLoading(true);
        const data = await SettingsService.getDataPolicy();
        setPolicyText(data.policy_text);
      } catch (error) {
        handleErrors(error);
      } finally {
        setLoading(false);
      }
    };
    loadPolicy();
  }, [show]);

  const handleAccept = useCallback(async () => {
    try {
      setLoading(true);
      await SettingsService.acceptDataPolicy();
      notifySuccess("Política de tratamiento de datos aceptada.");
      onAccepted?.();
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  }, [onAccepted]);

  return (
    <Modal show={show} backdrop="static" centered size="lg">
      <Modal.Header>
        <Modal.Title>Política de Tratamiento de Datos</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" />
            <p className="mt-2">Cargando política...</p>
          </div>
        ) : (
          <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
            {isPolicyUrl ? (
              <>
                <iframe
                  title="Política de Tratamiento de Datos"
                  src={policyText}
                  style={{ width: "100%", height: "400px", border: "none" }}
                  sandbox="allow-forms allow-popups allow-same-origin"
                />
                <p className="mt-2">
                  Si no se visualiza, ábrelo en otra pestaña:{" "}
                  <a
                    href={policyText}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {policyText}
                  </a>
                </p>
              </>
            ) : isPolicyHtml ? (
              <div dangerouslySetInnerHTML={{ __html: policyText }} />
            ) : (
              <p>{policyText}</p>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleAccept} disabled={loading}>
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" /> Aceptando...
            </>
          ) : (
            "Leído y acepto"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DataTreatmentPolicyModal;
